import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ApiError, api } from '../api/client.ts';
import {
  boardString,
  digitCounts,
  findClashes,
  formatTime,
  hasNotes,
  isFull,
  manualNotedCells,
  reducer,
  type GameState,
  type HintView,
} from '../game/state.ts';
import { LEVEL_INFO, type HintResponse } from '../game/types.ts';
import { storage, type Settings } from '../storage/storage.ts';
import { Board } from './Board.tsx';
import { Confetti } from './Confetti.tsx';
import { HintCard, NumberPad, Toolbar } from './Controls.tsx';
import { ConfirmDialog, NotesStartDialog, WinDialog } from './Dialogs.tsx';
import { BackIcon, HelpIcon, PauseIcon, PlayIcon, SettingsIcon } from './Icons.tsx';

interface GameProps {
  initial: GameState;
  settings: Settings;
  /** True while another layer (settings) is open, so keys go there instead. */
  modalOpen: boolean;
  /** How to play is open: the game pauses, and resumes when it closes. */
  helpOpen: boolean;
  onOpenSettings: () => void;
  onOpenHelp: () => void;
  /** Saves a changed setting (used by "Don't ask again" on the notes question). */
  onSettingsChange: (s: Settings) => void;
  onHome: () => void;
  onNewGame: () => void;
}

/** Moves save at once; the clock alone saves every few seconds. */
const TIME_SAVE_MS = 5000;

const NOTICE_REPEAT = 'Shown before, so not counted again.';
const NOTICE_CYCLING = "No new hints. Showing earlier ones again; these aren't counted.";
const MSG_CLASH = 'A digit is repeated on the board. Fix it first.';
const MSG_MISTAKE = 'There is a mistake on the board. Use Check to find it first.';

/** Everything worth saving except the clock, so saving on change doesn't run every tick. */
const fingerprint = (g: GameState) =>
  [g.values.join(''), g.notesKind, g.notes.join(','), g.struck.join(','), g.undo.length, g.redo.length,
    g.checksUsed, g.hintsUsed, g.seenHints.length, g.autoNotesUsed, g.notesMode, g.wrong.join(',')].join('|');

export function Game({ initial, settings, modalOpen, helpOpen, onOpenSettings, onOpenHelp, onSettingsChange, onHome, onNewGame }: GameProps) {
  const [game, dispatch] = useReducer(reducer, initial);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState<'check' | 'hint' | null>(null);
  const [asking, setAsking] = useState(false);
  const [confirming, setConfirming] = useState<'fill' | 'clear' | null>(null);
  const lastSave = useRef({ print: '', elapsed: 0 });
  const latestPause = useRef(initial.pauseReason);
  latestPause.current = game.pauseReason;
  const recorded = useRef(false);
  const playing = game.status === 'playing';
  const clashes = useMemo(() => findClashes(game.values), [game.values]);
  const layerOpen = modalOpen || helpOpen || asking || confirming !== null;

  // How to play pauses the clock while open and resumes it afterwards (unless paused for another reason).
  useEffect(() => {
    if (helpOpen) dispatch({ type: 'pause', reason: 'help' });
    else if (latestPause.current === 'help') dispatch({ type: 'resume' });
  }, [helpOpen]);

  // ---- saving: one game per level; a solved game is recorded in stats and removed ----
  useEffect(() => {
    if (game.status === 'won') {
      storage.clearGame(game.puzzle.level);
      if (!recorded.current) {
        recorded.current = true;
        storage.recordSolve({
          level: game.puzzle.level,
          puzzleId: game.puzzle.id,
          startedAt: game.startedAt,
          elapsedMs: game.elapsedMs,
          checksUsed: game.checksUsed,
          hintsUsed: game.hintsUsed,
          autoNotesUsed: game.autoNotesUsed,
          finishedAt: new Date().toISOString(),
        });
      }
      return;
    }
    const print = fingerprint(game);
    if (print !== lastSave.current.print || game.elapsedMs - lastSave.current.elapsed >= TIME_SAVE_MS) {
      storage.saveGame(game);
      lastSave.current = { print, elapsed: game.elapsedMs };
    }
  }, [game]);

  // Save on the way out too (closing the tab, going back).
  const latest = useRef(game);
  latest.current = game;
  useEffect(() => {
    const flush = () => latest.current.status !== 'won' && storage.saveGame(latest.current);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, []);

  // ---- timer: counts only while playing and on screen ----
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      dispatch({ type: 'tick', ms: now - last });
      last = now;
    }, 1000);
    return () => window.clearInterval(id);
  }, [playing]);

  // ---- auto-pause when the game goes off screen (tab hidden, minimised, app switched) ----
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') dispatch({ type: 'pause', reason: 'hidden' });
    };
    const onPageHide = () => dispatch({ type: 'pause', reason: 'hidden' });
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  // ---- toasts fade on their own ----
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(id);
  }, [toast]);

  const showError = (err: unknown) =>
    setToast(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');

  // ---- a full board is checked quietly: a win, or a nudge (mistakes stay hidden until Check) ----
  const full = isFull(game);
  const board = boardString(game);
  useEffect(() => {
    if (!full || !playing) return;
    if (clashes.cells.size) {
      setToast('The board is full, but a digit is repeated.');
      return;
    }
    let cancelled = false;
    api
      .check(game.puzzle.id, board)
      .then((r) => {
        if (cancelled) return;
        if (r.solved) dispatch({ type: 'won' });
        else setToast('The board is full, but something is off. Use Check to find it.');
      })
      .catch((err) => !cancelled && showError(err));
    return () => {
      cancelled = true;
    };
    // Runs when the board changes; other values are read fresh each time.
  }, [full, board]);

  const check = useCallback(async () => {
    if (!playing || busy) return;
    setBusy('check');
    try {
      const r = await api.check(game.puzzle.id, board);
      dispatch({ type: 'checked', wrongCells: r.wrongCells });
      if (r.solved) dispatch({ type: 'won' });
      else if (r.wrongCells.length === 0) setToast('No mistakes so far.');
      else setToast(r.wrongCells.length === 1 ? '1 cell is wrong.' : `${r.wrongCells.length} cells are wrong.`);
    } catch (err) {
      showError(err);
    } finally {
      setBusy(null);
    }
  }, [playing, busy, game.puzzle.id, board]);

  /** Hint shows the easiest step; Another hint shows one not seen before, then cycles. */
  const requestHint = useCallback(
    async (mode: 'first' | 'another') => {
      if (!playing || busy) return;
      if (clashes.cells.size) {
        dispatch({ type: 'hint', hint: { kind: 'message', text: MSG_CLASH } });
        return;
      }
      setBusy('hint');
      try {
        const after = game.hint?.kind === 'step' ? game.hint.step.key : undefined;
        const r: HintResponse = await api.hint(game.puzzle.id, board, { seen: game.seenHints, mode, after });
        let view: HintView | null = null;
        if (r.kind === 'step') {
          const notice = r.cycling ? NOTICE_CYCLING : r.repeat ? NOTICE_REPEAT : undefined;
          view = { kind: 'step', stage: 1, step: r, notice };
        } else if (r.kind === 'mistakes') view = { kind: 'message', text: MSG_MISTAKE };
        else if (r.kind === 'none') view = { kind: 'message', text: 'No hint is available for this position.' };
        if (view) dispatch({ type: 'hint', hint: view });
      } catch (err) {
        showError(err);
      } finally {
        setBusy(null);
      }
    },
    [playing, busy, clashes, game.hint, game.seenHints, game.puzzle.id, board],
  );

  /** First use of Notes in a game asks (or follows the setting); after that it just switches on and off. */
  const tapNotes = useCallback(() => {
    if (!playing) return;
    if (game.notesKind !== 'none' || game.notesMode) {
      dispatch({ type: 'toggleNotesMode' });
      return;
    }
    if (settings.notesStart === 'ask') setAsking(true);
    else dispatch({ type: 'startNotes', fill: settings.notesStart === 'fill' });
  }, [playing, game.notesKind, game.notesMode, settings.notesStart]);

  const requestFill = useCallback(() => {
    if (!playing || game.notesKind === 'auto') return;
    if (manualNotedCells(game) > 0) setConfirming('fill');
    else dispatch({ type: 'fillNotes' });
  }, [playing, game]);

  const requestClear = useCallback(() => {
    if (playing && hasNotes(game)) setConfirming('clear');
  }, [playing, game]);

  // ---- keyboard ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (layerOpen || game.status === 'won') return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key;
      if (game.status === 'paused') {
        if (k === ' ' || k === 'Enter' || k.toLowerCase() === 'p') {
          e.preventDefault();
          dispatch({ type: 'resume' });
        }
        return;
      }
      if (mod && k.toLowerCase() === 'z') {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? 'redo' : 'undo' });
      } else if (mod && k.toLowerCase() === 'y') {
        e.preventDefault();
        dispatch({ type: 'redo' });
      } else if (mod || e.altKey) {
        return;
      } else if (/^[1-9]$/.test(k)) {
        dispatch({ type: 'input', digit: Number(k) });
      } else if (k === 'Backspace' || k === 'Delete' || k === '0') {
        e.preventDefault();
        dispatch({ type: 'erase' });
      } else if (k.startsWith('Arrow')) {
        e.preventDefault();
        const d = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[k];
        if (d) dispatch({ type: 'move', dRow: d[0], dCol: d[1] });
      } else if (k.toLowerCase() === 'n') {
        tapNotes();
      } else if (k.toLowerCase() === 'a') {
        requestFill();
      } else if (k.toLowerCase() === 'p') {
        dispatch({ type: 'pause', reason: 'user' });
      } else if (k === 'Escape' && game.hint) {
        dispatch({ type: 'closeHint' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [layerOpen, game.status, game.hint, tapNotes, requestFill]);

  // Keep keyboard focus on the selected cell so screen readers follow it.
  useEffect(() => {
    if (game.selected === null || layerOpen || !playing) return;
    const el = document.querySelector<HTMLButtonElement>(`[data-cell="${game.selected}"]`);
    if (el && document.activeElement?.closest('.board')) el.focus({ preventScroll: true });
  }, [game.selected, layerOpen, playing]);

  const level = LEVEL_INFO[game.puzzle.level];
  const paused = game.status === 'paused';
  const hintOpen = !!game.hint && !paused;

  let message: string | null = null;
  if (paused) message = 'Paused. Resume to keep playing.';
  else if (toast) message = toast;
  const canFill = game.notesKind !== 'auto';
  const canClear = hasNotes(game);
  const notesLinks = !message && game.notesMode && playing;
  if (notesLinks && !canFill && !canClear) message = 'Notes on: numbers go in as pencil marks.';

  return (
    <div className={`game-screen${game.notesMode ? ' notes-mode' : ''}`}>
      <header className="game-bar">
        <button type="button" className="icon-btn" onClick={onHome} aria-label="Back to levels">
          <BackIcon />
        </button>
        <div className="game-title">
          <h1>{level.name}</h1>
          {settings.showTimer ? (
            <span className="timer" aria-label={`Time ${formatTime(game.elapsedMs)}`} role="timer">
              {formatTime(game.elapsedMs)}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={() => dispatch(paused ? { type: 'resume' } : { type: 'pause', reason: 'user' })}
          aria-label={paused ? 'Resume' : 'Pause'}
          disabled={game.status === 'won'}
          title={paused ? 'Resume (P)' : 'Pause (P)'}
        >
          {paused ? <PlayIcon /> : <PauseIcon />}
        </button>
        <button type="button" className="icon-btn" onClick={onOpenHelp} aria-label="How to play" title="How to play">
          <HelpIcon />
        </button>
        <button type="button" className="icon-btn" onClick={onOpenSettings} aria-label="Settings">
          <SettingsIcon />
        </button>
      </header>

      <main className="play-area">
        <div className="board-wrap">
          <Board game={game} clashCells={clashes.cells} onSelect={(cell) => dispatch({ type: 'select', cell })} />
          {paused ? (
            <div className="pause-cover">
              <p className="pause-title">Paused</p>
              <p className="pause-note">
                {game.pauseReason === 'hidden'
                  ? 'The game paused while it was out of view.'
                  : game.pauseReason === 'help'
                    ? 'Paused while How to play is open.'
                    : 'The board is hidden while paused.'}
              </p>
              <button type="button" className="btn primary" onClick={() => dispatch({ type: 'resume' })} autoFocus>
                <PlayIcon width={18} height={18} /> Resume
              </button>
            </div>
          ) : null}
        </div>

        <div className="side">
          {/* The message line and toolbar share one space; an open hint covers both. */}
          <div className={`dock${hintOpen ? ' has-hint' : ''}`}>
            <div className={`message-line${toast ? ' is-toast' : ''}`} aria-live="polite">
              {message ??
                (notesLinks ? (
                  <span className="notes-links">
                    {canFill ? (
                      <button type="button" className="link-btn" onClick={requestFill} title="Fill all possibilities (A)">
                        Fill all possibilities
                      </button>
                    ) : null}
                    {canClear ? (
                      <button type="button" className="link-btn" onClick={requestClear}>
                        Clear all notes
                      </button>
                    ) : null}
                  </span>
                ) : null)}
            </div>
            <Toolbar
              canUndo={game.undo.length > 0}
              canRedo={game.redo.length > 0}
              notesMode={game.notesMode}
              disabled={!playing}
              checking={busy === 'check'}
              hinting={busy === 'hint'}
              onUndo={() => dispatch({ type: 'undo' })}
              onRedo={() => dispatch({ type: 'redo' })}
              onNotes={tapNotes}
              onCheck={check}
              onHint={() => requestHint('first')}
            />
            {hintOpen && game.hint ? (
              <HintCard
                hint={game.hint}
                busy={busy === 'hint'}
                onAnother={() => requestHint('another')}
                onReveal={() => dispatch({ type: 'revealHint' })}
                onClose={() => dispatch({ type: 'closeHint' })}
              />
            ) : null}
          </div>
          <NumberPad
            counts={digitCounts(game.values)}
            clashDigits={clashes.digits}
            notesMode={game.notesMode}
            disabled={!playing}
            onDigit={(digit) => dispatch({ type: 'input', digit })}
            onErase={() => dispatch({ type: 'erase' })}
          />
        </div>
      </main>

      <NotesStartDialog
        open={asking}
        onChoose={(fillAll, remember) => {
          setAsking(false);
          if (remember) onSettingsChange({ ...settings, notesStart: fillAll ? 'fill' : 'blank' });
          dispatch({ type: 'startNotes', fill: fillAll });
        }}
        onCancel={() => setAsking(false)}
      />
      <ConfirmDialog
        open={confirming === 'fill'}
        title="Replace your notes?"
        body={`Filling all possibilities replaces your notes in ${manualNotedCells(game)} ${
          manualNotedCells(game) === 1 ? 'cell' : 'cells'
        }. Undo brings them back.`}
        confirmLabel="Replace notes"
        onConfirm={() => {
          setConfirming(null);
          dispatch({ type: 'fillNotes' });
        }}
        onCancel={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'clear'}
        title="Clear all notes?"
        body="Every note on the board will be removed. Undo brings them back."
        confirmLabel="Clear notes"
        onConfirm={() => {
          setConfirming(null);
          dispatch({ type: 'clearNotes' });
        }}
        onCancel={() => setConfirming(null)}
      />
      <WinDialog
        open={game.status === 'won'}
        level={game.puzzle.level}
        elapsedMs={game.elapsedMs}
        checksUsed={game.checksUsed}
        hintsUsed={game.hintsUsed}
        autoNotesUsed={game.autoNotesUsed}
        onNewGame={onNewGame}
        onHome={onHome}
      />
      {game.status === 'won' && settings.style === 'playful' ? <Confetti /> : null}
    </div>
  );
}
